import TypesenseInstantsearchAdapter from "typesense-instantsearch-adapter";

export interface AvailabilityConfig {
  typesenseHost: string;
  typesenseApiKey: string;
  supplierDiscoveryCollectionName: string;
  perPage?: number;
  searchRadius?: number;
  group_limit?: number;
}

export enum TypesenseCollection {
  SUPPLIER_INVENTORIES_RPN = "supplierinventories_rpn",
}

export interface PartAvailability {
  rootPartNumber: string;
  partNumber: string;
  quantity: number;
  supplierInfo: string;
  supplierName: string;
  supplierId: string;
  familyQuantity: number;
  location?: {
    lat: number;
    lng: number;
  };
  distanceMeters?: number;
  supplierDetails?: {
    businessName: string;
    address: string;
    contactInfo: string;
  };
}

export interface SupplierInfo {
  supplierName: string;
  supplierId: string;
  location?: {
    lat: number;
    lng: number;
  };
  distanceMeters?: number;
  familyQuantity: number;
  supplierDetails?: {
    businessName: string;
    address: string;
    contactInfo: string;
  };
}

export interface GroupedAvailability {
  groupKey: string;
  partNumber: string;
  rootPartNumber: string;
  partName: string;
  requestedQuantity: number;
  suppliersHavingPart: SupplierInfo[];
  totalSuppliers: number;
  quantityFulfilledPercentage: number;
  quantityFulfilledCount: number;
  supplierCoverageCount: number;
  coverageDistanceKm?: number;
  isFullyCovered: boolean;
}

export function createTypesenseAdapter(typesenseConfig: any) {
  return new TypesenseInstantsearchAdapter({
    server: {
      apiKey: typesenseConfig?.typesense_api_key,
      nodes: [
        {
          host: typesenseConfig?.typesense_host,
          port: parseInt(typesenseConfig?.port || "443"),
          protocol: typesenseConfig?.protocol || "https",
        },
      ],
    },
    additionalSearchParameters: {
      query_by: "rootPartNumber",
      group_by: "supplierInfo",
      group_limit: typesenseConfig?.supplier_discovery_parts_limit || 99,
      per_page: typesenseConfig?.supplier_discovery_suppliers_limit || 250,
      sort_by: "_group_found:desc",
    },
  });
}

export const getFullFillmentFilter = (preFacetedPartNumbers: any[]) => {
  const quantityGroups = new Map<number, string[]>();
  preFacetedPartNumbers.forEach((part: any) => {
    const quantity = part.quantity;
    if (!quantityGroups.has(quantity)) {
      quantityGroups.set(quantity, []);
    }
    quantityGroups.get(quantity)?.push(part.rootPartNumber);
  });

  const filterConditions = Array.from(quantityGroups.entries()).map(
    ([quantity, partNumbers]) => {
      return `(rootPartNumber:=[${partNumbers.join(
        ","
      )}] && familyQuantity:>=${quantity})`;
    }
  );

  return filterConditions.join(" || ");
};

export const getAvailablePartsFilter = (preFacetedPartNumbers: any[]) => {
  const partNumbers = preFacetedPartNumbers.map(
    (part: any) => part.rootPartNumber
  );
  return `rootPartNumber:=[${partNumbers.join(",")}]`;
};

export const fetchTopSupplierCandidates = async ({
  typesenseAdapter,
  supplierDiscoveryCollectionName,
  preFacetedPartNumbers,
  customerGeoPoint,
  supplier_discovery_parts_limit,
  selectedSupplierIds = [],
  perPage = 10,
  page = 1,
}: any): Promise<{
  supplierCandidateHits: any[];
  totalMatchingSupplierGroups: number;
  currentPage: number;
}> => {
  try {
    const baseFilter = getFullFillmentFilter(preFacetedPartNumbers);
    const userLatitude = customerGeoPoint?.latitude;
    const userLongitude = customerGeoPoint?.longitude;
    let finalFilter = baseFilter;
    if (selectedSupplierIds.length > 0) {
      const exclusionFilter = `supplierId:!=[${selectedSupplierIds.join(",")}]`;
      finalFilter = `${baseFilter} && ${exclusionFilter}`;
    }

    let sortBy = "_group_found:desc";
    if (userLatitude && userLongitude) {
      sortBy += `, location(${userLatitude},${userLongitude}):asc`;
    }

    const response = await typesenseAdapter.typesenseClient
      .collections(supplierDiscoveryCollectionName)
      .documents()
      .search({
        q: "*",
        query_by: "rootPartNumber",
        highlight_full_fields: "rootPartNumber",
        group_by: "supplierInfo",
        sort_by: sortBy,
        per_page: perPage,
        group_limit: supplier_discovery_parts_limit,
        page,
        filter_by: finalFilter,
      });

    const filteredHits = (response.grouped_hits ?? []).filter(
      (hit: any) => hit.found === preFacetedPartNumbers.length,
    );

    const supplierCandidateHits = filteredHits.map((hit: any) => ({
      ...hit,
      hits: hit.hits.map((item: any) => ({
        ...item.document,
        geo_location_meters: item.geo_location_meters,
        source: "Stock Sheet",
      })),
    }));

    return {
      supplierCandidateHits,
      totalMatchingSupplierGroups: response.found ?? 0,
      currentPage: response.page ?? page,
    };
  } catch (error) {
    console.error("Error fetching top supplier candidates:", error);
    return {
      supplierCandidateHits: [],
      totalMatchingSupplierGroups: 0,
      currentPage: page,
    };
  }
};

export const createPartNumberMapping = (
  preFacetedPartNumbers: Array<{
    rootPartNumber: string;
    partNumber: string;
    quantity: number;
    partName: string;
  }>
): Map<string, { partNumber: string; quantity: number; partName: string }> => {
  return new Map(
    preFacetedPartNumbers.map((part) => [
      part.rootPartNumber,
      {
        partNumber: part.partNumber,
        quantity: part.quantity,
        partName: part.partName,
      },
    ])
  );
};

export const processAvailabilityData = (
  response: any,
  preFacetedPartNumbers: Array<{
    rootPartNumber: string;
    partNumber: string;
    quantity: number;
    partName: string;
  }>
): GroupedAvailability[] => {
  const partNumberMapping = createPartNumberMapping(preFacetedPartNumbers);
  const foundPartNumbers = new Set(
    response.grouped_hits.map((group: any) => group.group_key[0])
  );

  const foundParts = response.grouped_hits.map((group: any) => {
    const rootPartNumber = group.group_key[0];
    const mappedPart = partNumberMapping.get(rootPartNumber);

    if (!mappedPart) {
      return {
        groupKey: rootPartNumber,
        partNumber: rootPartNumber,
        rootPartNumber,
        partName:
          preFacetedPartNumbers.find(
            (part) => part.rootPartNumber === rootPartNumber
          )?.partName || "",
        requestedQuantity: 0,
        suppliers: [],
        totalSuppliers: 0,
        quantityFulfilledPercentage: 0,
        supplierCoverageCount: 0,
        coverageDistanceKm: undefined,
        isFullyCovered: false,
      };
    }

    const suppliersHavingPart: SupplierInfo[] = group.hits.map((hit: any) => {
      const document = hit.document;
      const [supplierName, supplierId, coords] =
        document.supplierInfo.split("::");

      let location: { lat: number; lng: number } | undefined;
      if (coords) {
        const [lat, lng] = coords.split(",").map(Number);
        if (!isNaN(lat) && !isNaN(lng)) {
          location = { lat, lng };
        }
      }

      const supplierDetails = {
        businessName: supplierName,
      };

      return {
        supplierName,
        supplierId,
        location,
        distanceMeters: hit.geo_distance_meters?.location,
        familyQuantity: document.familyQuantity,
        source: "Stock Sheet",
        supplierDetails,
      };
    });

    let cumulativeQuantity = 0;
    let supplierCoverageCount = 0;
    let coverageDistanceKm: number | undefined;

    for (let i = 0; i < suppliersHavingPart.length; i++) {
      const supplier = suppliersHavingPart[i];
      const availableQuantity = Math.min(
        supplier.familyQuantity,
        mappedPart.quantity - cumulativeQuantity
      );
      cumulativeQuantity += availableQuantity;
      supplierCoverageCount++;

      if (supplier.distanceMeters !== undefined) {
        coverageDistanceKm = supplier.distanceMeters / 1000;
      }

      if (cumulativeQuantity >= mappedPart.quantity) {
        break;
      }
    }

    const quantityFulfilledPercentage =
      mappedPart.quantity > 0
        ? Math.min(
            100,
            Math.floor((cumulativeQuantity / mappedPart.quantity) * 100)
          )
        : 0;

    const isFullyCovered = cumulativeQuantity >= mappedPart.quantity;

    return {
      groupKey: rootPartNumber,
      partNumber: mappedPart.partNumber,
      rootPartNumber,
      partName: mappedPart.partName,
      requestedQuantity: mappedPart.quantity,
      suppliersHavingPart,
      totalSuppliers: suppliersHavingPart.length,
      quantityFulfilledPercentage,
      quantityFulfilledCount: cumulativeQuantity,
      supplierCoverageCount: supplierCoverageCount,
      coverageDistanceKm: coverageDistanceKm,
      isFullyCovered,
    };
  });

  const missingParts: GroupedAvailability[] = preFacetedPartNumbers
    .filter((part) => !foundPartNumbers.has(part.rootPartNumber))
    .map((part) => ({
      groupKey: part.rootPartNumber,
      partNumber: part.partNumber,
      rootPartNumber: part.rootPartNumber,
      partName: part.partName || "",
      requestedQuantity: part.quantity,
      suppliersHavingPart: [],
      totalSuppliers: 0,
      quantityFulfilledPercentage: 0,
      quantityFulfilledCount: 0,
      supplierCoverageCount: 0,
      coverageDistanceKm: undefined,
      isFullyCovered: false,
    }));

  return [...foundParts, ...missingParts];
};

export const fetchAvailabilityData = async (
  preFacetedPartNumbers: Array<{
    rootPartNumber: string;
    partNumber: string;
    quantity: number;
    partName: string;
  }>,
  typesenseConfig: any,
  customerGeoPoint?: { latitude: number; longitude: number }
): Promise<GroupedAvailability[]> => {
  try {
    const filter = getAvailablePartsFilter(preFacetedPartNumbers);

    let sortBy = "";
    if (customerGeoPoint?.latitude && customerGeoPoint?.longitude) {
      sortBy += `location(${customerGeoPoint.latitude},${customerGeoPoint.longitude}):asc`;
    }

    const response = await createTypesenseAdapter(typesenseConfig)
      .typesenseClient.collections(TypesenseCollection.SUPPLIER_INVENTORIES_RPN)
      .documents()
      .search(
        {
          q: "*",
          query_by: "rootPartNumber",
          filter_by: filter,
          group_by: "rootPartNumber",
          sort_by: sortBy,
          page: 1,
          group_limit: typesenseConfig?.supplier_discovery_parts_limit,
          per_page: typesenseConfig?.supplier_discovery_suppliers_limit,
        },
        {}
      );

    return processAvailabilityData(response, preFacetedPartNumbers);
  } catch (error) {
    console.error("Error fetching availability data:", error);
    throw new Error("Failed to fetch availability data");
  }
};
